import { useEffect, useRef, useState } from 'react';

import { useGSAP } from '@/hooks/useGSAP';

interface CategoryState {
  activeIndex: number;
  loadingProgress: number[];
}

export const useGsapCategoryScroll = (
  categoryCount: number,
  onIndexChange?: (index: number) => void
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<(HTMLElement | null)[]>([]);
  const [state, setState] = useState<CategoryState>({
    activeIndex: 0,
    loadingProgress: Array(categoryCount).fill(0),
  });

  const { ScrollTrigger, ready } = useGSAP();

  // Ref pour éviter les appels multiples
  const lastNotifiedIndexRef = useRef<number>(0);

  useEffect(() => {
    if (!ready || !ScrollTrigger) return;
    if (!containerRef.current || categoryCount === 0) return;

    let lastUpdateTime = 0;
    const UPDATE_THROTTLE = 16; // ~60fps throttle

    const scrolltrigger = ScrollTrigger.create({
      trigger: containerRef.current,
      start: 'top top',
      end: 'bottom bottom +=200',
      scrub: 2,
      markers: false,
      onUpdate: (self) => {
        const now = Date.now();
        if (now - lastUpdateTime < UPDATE_THROTTLE) return;
        lastUpdateTime = now;

        // Progression du scroll (0 à 1) basée sur la hauteur du container
        const scrollPos = self.progress;

        // Calculer l'index actif basé sur la progression
        const newActiveIndex = Math.min(
          Math.floor(scrollPos * categoryCount),
          categoryCount - 1
        );

        // Calculer la progression pour chaque catégorie
        const newProgress = Array(categoryCount)
          .fill(0)
          .map((_, index) => {
            const categoryStart = index / categoryCount;
            const categoryEnd = (index + 1) / categoryCount;

            // Avant cette catégorie : 0%
            if (scrollPos < categoryStart) return 0;

            // Après cette catégorie : 100%
            if (scrollPos >= categoryEnd) return 100;

            // Pendant cette catégorie : progression linéaire de 0 à 100
            const categoryProgress =
              (scrollPos - categoryStart) / (categoryEnd - categoryStart);
            return categoryProgress * 100;
          });

        // Mettre à jour l'état
        setState({
          activeIndex: newActiveIndex,
          loadingProgress: newProgress,
        });

        // Notifier le changement d'index de manière asynchrone
        if (lastNotifiedIndexRef.current !== newActiveIndex) {
          lastNotifiedIndexRef.current = newActiveIndex;

          // Utiliser queueMicrotask pour différer l'appel
          queueMicrotask(() => {
            onIndexChange?.(newActiveIndex);
          });
        }
      },
    });

    return () => {
      scrolltrigger.kill();
    };
  }, [categoryCount, onIndexChange, ready, ScrollTrigger]);

  const registerCategoryRef = (index: number) => (el: HTMLElement | null) => {
    categoryRefs.current[index] = el;
  };

  const getLoadingBarWidth = (index: number): string => {
    return `${state.loadingProgress[index] || 0}%`;
  };

  const isCategoryActive = (index: number): boolean => {
    return state.activeIndex === index;
  };

  return {
    containerRef,
    registerCategoryRef,
    activeIndex: state.activeIndex,
    getLoadingBarWidth,
    isCategoryActive,
  };
};
